// The cooking layout, /recipes/:id/cook (redesign slice 4, wireframe 7):
// readable at arm's length, ingredients to tick off as they're gathered,
// one current step at a time, and the screen kept awake.
//
// Opened from a planned meal (Home's Today), the URL carries the meal --
// ?week=YYYY-MM-DD&day=Saturday&slot=dinner -- so the page can start at the
// meal's planned servings (its People served plus any leftovers drawing on
// it; planScaling.ts) and offer the meal's other dishes. Opened from the
// recipe page it starts at the recipe's own servings.
//
// Ticks and the current step last for the browser session (sessionStorage),
// per recipe. "Done cooking" marks the meal's dishes cooked (slice 7c,
// 0058; not its leftovers, which were cooked before) when it was opened from
// the plan, then returns Home.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useWakeLock } from "../hooks/useWakeLock";
import { MoreMenu } from "../components/MoreMenu";
import { ServingsStepper } from "../components/RecipeParts";
import { sectionHeading, smallLabel, supporting } from "../components/recipeStyles";
import { COLORS, DAYS, FONT_SIZE, LAYOUT, MEAL_SLOTS, RADIUS, RECIPE_TYPE_LABELS, SPACE } from "../lib/constants";
import { HOME_PATH } from "../lib/navigation";
import { ingredientLines, mealDishes, scaleFactor, stepLines, timeParts } from "../lib/recipeDisplay";
import { drawsOn, mealKey, mealLabel, plannedServings, weekServings } from "../lib/planScaling";
import type { Day, MealSlot } from "../lib/types";

interface CookProgress {
  gathered: string[]; // ingredient ids
  done: number; // how many steps are done; the next one is current
}

const storageKey = (recipeId: string) => `wk.cook.${recipeId}`;

function readProgress(recipeId: string): CookProgress {
  try {
    const raw = sessionStorage.getItem(storageKey(recipeId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Array.isArray(parsed.gathered) && typeof parsed.done === "number") return parsed;
  } catch {
    // Storage blocked or unreadable: start fresh.
  }
  return { gathered: [], done: 0 };
}

function writeProgress(recipeId: string, progress: CookProgress) {
  try {
    sessionStorage.setItem(storageKey(recipeId), JSON.stringify(progress));
  } catch {
    // Not saved; the page still works for as long as it stays open.
  }
}

// Keyed by recipe so switching dishes starts a fresh page with that dish's
// own progress, rather than carrying state across.
export function CookPage() {
  const { id = "" } = useParams();
  return <CookRecipe key={id} id={id} />;
}

function CookRecipe({ id }: { id: string }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const awake = useWakeLock();
  const mp = useMealPlanner();
  const recipe = mp.recipeById(id);

  // The meal this was opened from, if any.
  const week = params.get("week") || "";
  const day = params.get("day") as Day | null;
  const slot = params.get("slot") as MealSlot | null;
  const meal = week && day && (DAYS as readonly string[]).includes(day) && slot && (MEAL_SLOTS as readonly string[]).includes(slot) ? { week, day, slot } : null;
  const dishes = meal ? mealDishes(mp.plansByWeek, meal.week, meal.day, meal.slot, (rid) => !!mp.recipeById(rid)) : [];
  // This recipe's place in the meal, and who it's cooked for. Leftovers
  // aren't cooked again, so they're only ever for the meal's own people.
  const dish = meal && dishes.find((d) => d.recipeId === id);
  const mealRef = meal && dish ? { weekStart: meal.week, day: meal.day, mealSlot: meal.slot, dishType: dish.dishType } : null;
  const isLeftovers = !!(mealRef && mp.plansByWeek[mealRef.weekStart]?.leftovers?.[mealKey(mealRef.day, mealRef.mealSlot, mealRef.dishType)]);
  const planned = mealRef ? (isLeftovers ? weekServings(mp.plansByWeek, mealRef.weekStart) : plannedServings(mp.plansByWeek, mealRef)) : undefined;
  const leftoverDraws = mealRef && !isLeftovers ? drawsOn(mp.plansByWeek, mealRef).filter((d) => d.scalesSource) : [];

  const [progress, setProgress] = useState<CookProgress>(() => readProgress(id));
  const [servings, setServings] = useState<number | null>(null);
  useEffect(() => writeProgress(id, progress), [id, progress]);

  if (!recipe) {
    return (
      <div className="mp-sans" style={{ maxWidth: 560 }}>
        <p style={{ fontSize: FONT_SIZE.base, color: COLORS.soft }}>This recipe isn't in your library.</p>
        <Link to={HOME_PATH} style={{ color: COLORS.accent, fontWeight: 700, fontSize: FONT_SIZE.base }}>
          Go Home
        </Link>
      </div>
    );
  }

  const yieldType = recipe.recipeType === "preserves" || recipe.recipeType === "medicinal";
  // Never below the recipe's own servings: scaling only goes up.
  const shown = servings ?? (planned && !yieldType ? Math.max(planned, recipe.servings) : recipe.servings);
  const plannedReason =
    mealRef && planned
      ? [
          `${weekServings(mp.plansByWeek, mealRef.weekStart)} at this meal`,
          ...leftoverDraws.map((d) => `${weekServings(mp.plansByWeek, d.ref.weekStart)} as leftovers for ${mealLabel(d.ref, new Date())}`),
        ].join(" + ")
      : "";
  const factor = yieldType ? 1 : scaleFactor(recipe.servings, shown);
  const ingredients = ingredientLines(recipe.ingredients || [], factor);
  const items = ingredients.filter((l) => l.kind === "ingredient");
  const gathered = new Set(progress.gathered);
  const steps = stepLines(recipe.instructions || []);
  const stepCount = steps.filter((l) => l.kind === "step").length;
  const times = timeParts(recipe);
  const cameFromApp = (window.history.state?.idx ?? 0) > 0;

  const toggleGathered = (ingredientId: string) =>
    setProgress((p) => ({ ...p, gathered: p.gathered.includes(ingredientId) ? p.gathered.filter((x) => x !== ingredientId) : [...p.gathered, ingredientId] }));
  // Tapping the current step marks it done; tapping any other step makes it
  // the current one (to go back, or skip ahead).
  const tapStep = (number: number) => setProgress((p) => ({ ...p, done: number === p.done + 1 ? number : number - 1 }));

  function finish() {
    if (meal) {
      const week = mp.plansByWeek[meal.week];
      dishes.filter((d) => !week?.leftovers?.[mealKey(meal.day, meal.slot, d.dishType)]).forEach((d) => mp.setCooked(meal.week, meal.day, meal.slot, d.dishType, true));
    }
    try {
      sessionStorage.removeItem(storageKey(recipe!.id));
    } catch {
      // Nothing to clear.
    }
    navigate(HOME_PATH);
  }

  const ingredientsBlock = (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.sm, minWidth: 0 }}>
      <h2 style={sectionHeading}>
        Ingredients{items.length > 0 && <span style={{ ...supporting, fontFamily: "var(--font-sans)", fontSize: FONT_SIZE.base }}> · {items.filter((l) => gathered.has(l.id)).length} of {items.length} gathered</span>}
      </h2>
      {ingredients.length === 0 && <p style={{ ...supporting, margin: 0 }}>No ingredients listed.</p>}
      {ingredients.map((line) => {
        if (line.kind === "header") return <div key={line.key} style={{ ...smallLabel, color: COLORS.accent, marginTop: SPACE.md }}>{line.label}</div>;
        if (line.kind === "note") return <div key={line.key} style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", color: COLORS.soft, fontSize: FONT_SIZE.base }}>{line.text}</div>;
        const checked = gathered.has(line.id);
        return (
          <label
            key={line.key}
            style={{ display: "flex", alignItems: "center", gap: SPACE.xl, minHeight: LAYOUT.tapTarget + 8, borderBottom: `1px solid ${COLORS.line}`, cursor: "pointer", fontSize: FONT_SIZE.xl, color: checked ? COLORS.soft : COLORS.ink }}
          >
            <input type="checkbox" className="mp-check" checked={checked} onChange={() => toggleGathered(line.id)} style={{ width: 24, height: 24, flexShrink: 0 }} />
            <span style={{ textDecoration: checked ? "line-through" : "none" }}>
              {line.amount && <strong style={{ fontVariantNumeric: "tabular-nums lining-nums" }}>{line.amount} </strong>}
              {line.text}
            </span>
          </label>
        );
      })}
    </section>
  );

  const current = progress.done + 1;
  const stepsBlock = (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.md, minWidth: 0 }}>
      <h2 style={sectionHeading}>
        Steps
        {stepCount > 0 && (
          <span style={{ ...supporting, fontFamily: "var(--font-sans)", fontSize: FONT_SIZE.base }}> · {progress.done >= stepCount ? "all done" : `${current} of ${stepCount}`}</span>
        )}
      </h2>
      {steps.length === 0 && <p style={{ ...supporting, margin: 0 }}>No instructions added yet.</p>}
      {steps.map((line) => {
        if (line.kind === "header") return <div key={line.key} style={{ ...smallLabel, color: COLORS.accent, marginTop: SPACE.md }}>{line.label}</div>;
        if (line.kind === "note") return <div key={line.key} style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", color: COLORS.soft, fontSize: FONT_SIZE.base, paddingLeft: SPACE.x4 }}>{line.text}</div>;
        const isCurrent = line.number === current;
        const isDone = line.number <= progress.done;
        return (
          <button
            key={line.key}
            type="button"
            className="mp-btn"
            aria-current={isCurrent ? "step" : undefined}
            onClick={() => tapStep(line.number)}
            style={{
              display: "grid",
              gridTemplateColumns: "28px minmax(0, 1fr)",
              columnGap: SPACE.xl,
              alignItems: "baseline",
              textAlign: "left",
              width: "100%",
              padding: isCurrent ? "var(--space-xxl)" : "var(--space-xl) var(--space-xxl)",
              borderRadius: RADIUS.xl,
              background: isCurrent ? COLORS.accentTint : "transparent",
              color: isDone ? COLORS.soft : COLORS.ink,
              fontSize: isCurrent ? FONT_SIZE.h3 : FONT_SIZE.xl,
              lineHeight: 1.4,
            }}
          >
            <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, color: isDone ? COLORS.soft : COLORS.accent, fontVariantNumeric: "lining-nums", textAlign: "right" }}>{isDone ? "✓" : line.number}</span>
            <span>
              {isCurrent && <span style={{ ...smallLabel, display: "block", color: COLORS.accent, marginBottom: SPACE.xs }}>Now · tap when done</span>}
              {line.text}
            </span>
          </button>
        );
      })}
    </section>
  );

  return (
    <article className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, maxWidth: 1000, marginBottom: SPACE.x4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md }}>
        <button
          type="button"
          className="mp-btn"
          onClick={() => (cameFromApp ? navigate(-1) : navigate(HOME_PATH))}
          style={{ minHeight: LAYOUT.tapTarget, padding: 0, background: "transparent", color: COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700 }}
        >
          ‹ {cameFromApp ? "Back" : "Home"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.lg }}>
          {awake && <span style={supporting}>Screen stays awake</span>}
          <MoreMenu label="Recipe menu" items={[{ label: "Open the recipe page", onSelect: () => navigate(`/recipes/${recipe.id}`) }]} />
        </div>
      </div>

      {dishes.length > 1 && (
        <div role="group" aria-label="Dishes in this meal" style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
          {dishes.map((d) => {
            const pressed = d.recipeId === recipe.id;
            const label = RECIPE_TYPE_LABELS[d.dishType];
            return (
              <button
                key={d.dishType}
                type="button"
                className="mp-btn"
                aria-pressed={pressed}
                onClick={() => !pressed && navigate(`/recipes/${d.recipeId}/cook?${params.toString()}`, { replace: true })}
                style={{
                  minHeight: LAYOUT.tapTarget,
                  padding: "0 var(--space-xxl)",
                  borderRadius: RADIUS.pill,
                  border: `1px solid ${pressed ? COLORS.accent : COLORS.line}`,
                  background: pressed ? COLORS.accentTint : COLORS.card,
                  color: pressed ? COLORS.accent : COLORS.ink,
                  fontSize: FONT_SIZE.base,
                  fontWeight: 700,
                }}
              >
                {d.dishType === "entree" ? label : `${label}: ${mp.recipeById(d.recipeId)?.title ?? ""}`}
              </button>
            );
          })}
        </div>
      )}

      <header style={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.display, fontWeight: 600, lineHeight: 1.1 }}>{recipe.title || "Untitled recipe"}</h1>
        {times.total && (
          <div style={{ fontSize: FONT_SIZE.base }}>
            {times.parts.join(" · ")}
            {times.parts.length > 1 && (
              <>
                {" · "}
                <strong>{times.total}</strong>
              </>
            )}
          </div>
        )}
        {!yieldType && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.xl, flexWrap: "wrap", borderTop: `1px solid ${COLORS.line}`, borderBottom: `1px solid ${COLORS.line}`, padding: "var(--space-md) 0" }}>
            <span style={{ display: "flex", flexDirection: "column" }}>
              <strong>Makes {shown} servings</strong>
              <span style={supporting}>
                {servings === null && plannedReason
                  ? `${plannedReason}${factor === 1 ? "" : ` (recipe makes ${recipe.servings})`}`
                  : factor === 1
                    ? "As written"
                    : `Scaled from ${recipe.servings}`}
              </span>
            </span>
            <ServingsStepper servings={shown} label="" onChange={(n) => setServings(n)} />
          </div>
        )}
      </header>

      <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? SPACE.x4 : SPACE.x5, alignItems: "flex-start" }}>
        <div style={{ flex: "2 1 300px", minWidth: 0 }}>{ingredientsBlock}</div>
        <div style={{ flex: "3 1 380px", minWidth: 0 }}>{stepsBlock}</div>
      </div>

      <div style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.x3 }}>
        <button
          type="button"
          className="mp-btn"
          onClick={finish}
          style={{ minHeight: 52, width: isMobile ? "100%" : "auto", padding: "0 var(--space-x4)", borderRadius: RADIUS.md, background: "transparent", color: COLORS.accent, border: `1.5px solid ${COLORS.accent}`, fontSize: FONT_SIZE.base, fontWeight: 700 }}
        >
          Done cooking
        </button>
      </div>
    </article>
  );
}
